import { InputPasswordHideSVG, InputPasswordShowSVG } from "@/assets/svg";
import { errorToast } from "@/utils/toast";
import { login } from "@store/slices/authSlice";
import React from "react";
import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router";

interface SignInFormData {
  email: string;
  password: string;
}

interface SignInFormProps {
  onForgotPasswordClick: () => void;
}

const SignInForm = ({ onForgotPasswordClick }: SignInFormProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = React.useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting, isValid },
  } = useForm<SignInFormData>({
    mode: "all",
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const onSubmit = async (data: SignInFormData) => {
    try {
      // Dispatch returns a promise with a specific structure
      const resultAction = await dispatch(login(data) as any);

      // Check if the action was fulfilled or rejected
      if (login.fulfilled.match(resultAction)) {
        console.log("Login successful, navigating to dashboard");
        navigate("/dashboard");
      } else if (login.rejected.match(resultAction)) {
        // This will catch the rejection from createAsyncThunk
        console.error("Login failed with error:", resultAction.error);
        // The toast is already handled in the authSlice, but we can add additional handling here
      }
    } catch (error) {
      // This catch block will only execute if there's an error in the code above,
      // not for login failures which are handled by the thunk
      console.error("Unexpected error in onSubmit:", error);
      errorToast("An unexpected error occurred", "Please try again later.");
    }
  };

  return (
    <>
      <h1 className="text-[40px] text-[#232323] font-bold mb-2">Sign in</h1>
      <p className="text-[18px] text-[#969696] font-normal mb-8">
        Please sign in to continue to your account.
      </p>

      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <div className="mb-6">
          <label
            htmlFor="email"
            className="block mb-2 text-[#1e1e1e] font-[500] text-[14px]"
          >
            Email address
          </label>
          <input
            type="email"
            id="email"
            {...register("email", {
              required: "Email is required",
              pattern: {
                value: /\S+@\S+\.\S+/,
                message: "Please enter a valid email address",
              },
            })}
            placeholder="Enter your email address"
            className={`w-full h-[42px] px-[10px] py-3 border rounded-[6px] text-[14px] text-[#1e1e1e] font-[400] placeholder:text-[#B3B3B3] ${
              errors.email
                ? "border-[#FF3B30]"
                : "border-[#E0E0E0] focus:border-[#73b5c5]"
            } focus:outline-none focus:shadow-[0_0_0_2px_rgba(0,136,204,0.1)]`}
          />
          {errors.email && (
            <span className="text-[#FF3B30] text-[12px] font-[500] mt-0 block">
              {errors.email.message}
            </span>
          )}
        </div>

        <div className="mb-6">
          <label
            htmlFor="password"
            className="block mb-2 text-[#1e1e1e] font-[500] text-[14px]"
          >
            Password
          </label>
          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              id="password"
              {...register("password", {
                required: "Password is required",
                minLength: {
                  value: 6,
                  message: "Password must be at least 6 characters",
                },
              })}
              placeholder="Enter your password"
              className={`w-full h-[42px] px-[10px] py-3 border rounded-[6px] text-[14px] text-[#1e1e1e] font-[400] placeholder:text-[#B3B3B3] ${
                errors.password
                  ? "border-[#FF3B30]"
                  : "border-[#E0E0E0] focus:border-[#73b5c5]"
              } focus:outline-none focus:shadow-[0_0_0_2px_rgba(0,136,204,0.1)]`}
            />
            <button
              type="button"
              className="absolute cursor-pointer right-3 top-1/2 transform -translate-y-1/2 text-[#B3B3B3] hover:text-[#73b5c5] focus:outline-none"
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? (
                <InputPasswordHideSVG />
              ) : (
                <InputPasswordShowSVG />
              )}
            </button>
          </div>
          {errors.password && (
            <span className="text-[#FF3B30] text-[12px] font-[500] mt-0 block">
              {errors.password.message}
            </span>
          )}
          <div className="text-right mt-[5px] mb-8">
            <button
              type="button"
              onClick={onForgotPasswordClick}
              className="text-[#1e1e1e] text-sm font-[400] no-underline hover:underline cursor-pointer"
            >
              Forgot password?
            </button>
          </div>
        </div>

        <button
          type="submit"
          className="w-full h-[42px] bg-[#73b5c5] text-white font-[600] border-none rounded-[4px] text-[18px] cursor-pointer transition-colors hover:bg-[#5a9aaa] disabled:bg-[#9e9e9e] disabled:cursor-not-allowed"
          disabled={
            isSubmitting ||
            !isValid ||
            !watch("email") ||
            !watch("password") ||
            Object.keys(errors).length > 0
          }
        >
          {isSubmitting ? "Signing in..." : "Sign in"}
        </button>
      </form>
    </>
  );
};

export default SignInForm;
